/**
 * self-serve-intent.js — fire-and-forget courtesy ping to MC's Zoho
 * whenever a member clicks a "Reserve direct" button (the anchor that
 * opens the venue's own booking site — OpenTable, Resy, hotel site,
 * gym site, etc.) from inside an `?embed=widget&tenant=mindescapes`
 * iframe.
 *
 * Contract (agreed with MindEscapes engineering 01 Jul 2026):
 *   • `kind: "self_serve_intent"` on top level, not inside `request`
 *   • HMAC secret stays server-side — this helper hits our own
 *     `/api/lc/self-serve-intent`; the backend signs & forwards
 *   • `keepalive: true` on the fetch so the ping survives the tab
 *     navigating away to the venue's booking site
 *   • Silently no-ops for non-MC traffic (backend enforces the tenant
 *     whitelist; the helper still calls unconditionally so we can
 *     never accidentally forget)
 *   • Never throws — every step is try/catch-wrapped; the click flow
 *     is never blocked by a failure here
 *
 * FE fire audit (added 03 Jul 2026, per MindEscapes engineer request):
 *   Before every intent call, we ALSO fire a lightweight audit to
 *   /api/lc/self-serve-intent-fire-audit so we can prove the click
 *   actually landed at LC's door. This runs even for non-MC traffic
 *   (LC-native clicks are still worth counting for diagnostics).
 *   navigator.sendBeacon is used when available (survives tab unload).
 *
 * Usage:
 *   import { fireSelfServeIntent } from "@/lib/self-serve-intent";
 *   <a
 *     href={venue.reservation_url}
 *     target="_blank" rel="noopener noreferrer"
 *     onClick={() => fireSelfServeIntent({
 *       benefit: "table",
 *       venue,
 *       extra: { date, time, guests },   // optional
 *     })}
 *   >Reserve direct</a>
 */
const API = process.env.REACT_APP_BACKEND_URL || "";

const _MC_TENANTS = new Set(["mindescapes", "aurelion"]);

function _readUrlParams() {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Reads tenant + member identity from the current iframe URL params.
 * Returns null when we're not inside an MC-tenanted iframe.
 */
function _readTenantContext() {
  const sp = _readUrlParams();
  const tenant = (sp.get("tenant") || "").toLowerCase();
  if (!_MC_TENANTS.has(tenant)) return null;
  return {
    tenant,
    member_card:  sp.get("member_card") || sp.get("card_no") || null,
    member_email: sp.get("member_email") || null,
    member_first: sp.get("member_first") || sp.get("first_name") || null,
    member_last:  sp.get("member_last")  || sp.get("last_name")  || null,
    session_id:   sp.get("session_id")   || null,
  };
}

/**
 * Reads whatever tenant/member context is on the URL, MC or not.
 * Used only by the FE fire audit — that audit counts every click,
 * including LC-native ones, so we cannot early-exit on non-MC.
 */
function _readAnyTenantContext() {
  const sp = _readUrlParams();
  return {
    tenant:       (sp.get("tenant") || "").toLowerCase() || null,
    member_card:  sp.get("member_card") || sp.get("card_no") || null,
    session_id:   sp.get("session_id")   || null,
  };
}

/**
 * Fire the FE audit row · returns immediately (best-effort).
 * Uses navigator.sendBeacon when available so the ping survives the
 * tab navigating away to the venue's booking site; falls back to
 * fetch(keepalive) when Beacon isn't available (older browsers).
 * Never throws.
 */
function _fireAuditRow({ benefit, venue, requestId }) {
  try {
    const ctx = _readAnyTenantContext();
    const body = {
      benefit:    benefit || null,
      tenant:     ctx.tenant,
      venue_slug: (venue && (venue.slug || venue.venue_slug)) || null,
      venue_name: (venue && (venue.name || venue.title)) || null,
      venue_id:   (venue && (venue.place_id || venue.id)) || null,
      city:       (venue && venue.city) || null,
      country:    (venue && venue.country) || null,
      member_card: ctx.member_card,
      session_id:  ctx.session_id,
      page_url:    (typeof window !== "undefined") ? window.location.href : null,
      referrer:    (typeof document !== "undefined") ? document.referrer || null : null,
      request_id:  requestId,
      client_ts:   new Date().toISOString(),
    };

    const url = `${API}/api/lc/self-serve-intent-fire-audit`;
    const jsonBody = JSON.stringify(body);

    // sendBeacon requires a Blob with the right content-type · it does
    // NOT accept a plain string. It also cannot be awaited or read.
    if (typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([jsonBody], { type: "application/json" });
        const queued = navigator.sendBeacon(url, blob);
        if (queued) return;   // Beacon accepted · no fallback needed
      } catch { /* fall through to fetch */ }
    }

    // Fallback · fetch with keepalive (survives tab navigation for
    // small payloads · this one is <1 KB).
    fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    jsonBody,
      keepalive: true,
    }).catch(() => { /* swallow · audit is courtesy */ });
  } catch {
    // Anything unexpected · swallow. Audit must never impact the click.
  }
}

/**
 * Shape a venue card object into the `request` block MC's Zoho will
 * render. Field names are snake_case; the receiver renders them as
 * ticket labels via `provider_name` → "Provider Name".
 */
function _venueToRequest(benefit, venue, extra) {
  const request = { action: "direct" };
  if (!venue || typeof venue !== "object") return { ...request, ...(extra || {}) };

  // Common
  if (venue.name)     request.provider_name = venue.name;
  if (venue.place_id || venue.id) {
    request.provider_id = venue.place_id || venue.id;
  }
  if (venue.city)     request.city    = venue.city;
  if (venue.country)  request.country = venue.country;

  // Per-benefit label (MC's Zoho renders this as-is)
  if (benefit === "table") {
    if (venue.name)          request.restaurant       = venue.name;
    if (venue.booking_platform || venue.resolved_platform?.platform) {
      request.booking_platform = venue.booking_platform || venue.resolved_platform.platform;
    }
    if (venue.resolved_platform?.url || venue.verified_reservation_url) {
      request.destination_url = venue.resolved_platform?.url || venue.verified_reservation_url;
    }
  } else if (benefit === "stay") {
    if (venue.name) request.hotel = venue.name;
  } else if (benefit === "fit") {
    if (venue.name) request.venue = venue.name;
    if (venue.category) request.category = venue.category;
  } else if (benefit === "enjoy") {
    if (venue.name || venue.title) request.event = venue.title || venue.name;
  } else if (benefit === "exclusive") {
    if (venue.name) request.house = venue.name;
    if (venue.category) request.category = venue.category;
  }

  // Extra fields the caller passes (date, time, guests, occasion, notes)
  if (extra && typeof extra === "object") {
    Object.assign(request, extra);
  }
  return request;
}

/**
 * Generate a per-click correlation id · pairs the FE fire audit row
 * with the /api/lc/self-serve-intent that follows.
 */
function _clickId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* noop */ }
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Main entrypoint. Never throws. Never awaited by the caller.
 *
 * @param {object} params
 * @param {string} params.benefit  — pillar canonical key: table/stay/fit/enjoy/exclusive/...
 * @param {object} params.venue    — the card object (name, city, place_id, etc.)
 * @param {object} [params.extra]  — optional overrides / caller-collected fields
 */
export function fireSelfServeIntent({ benefit, venue, extra } = {}) {
  // Correlation id · same value on both audit rows.
  const requestId = _clickId();

  // (1) FE fire audit · proves the click landed at LC's door.
  // Runs for ALL traffic (MC and LC-native alike).
  _fireAuditRow({ benefit, venue, requestId });

  try {
    const ctx = _readTenantContext();
    if (!ctx) return;      // Non-MC traffic → skip the MC forward.

    const payload = {
      tenant:   ctx.tenant,
      benefit:  benefit || "table",
      member: {
        member_card: ctx.member_card,
        email:       ctx.member_email,
        first_name:  ctx.member_first,
        last_name:   ctx.member_last,
      },
      request:    _venueToRequest(benefit || "table", venue, extra),
      source:     "lesconcierges-self-serve",
      page_url:   (typeof window !== "undefined") ? window.location.href : null,
      session_id: ctx.session_id,
      request_id: requestId,
    };

    // Fire-and-forget · keepalive lets the ping survive tab navigation.
    // We do NOT await this — the caller's <a target="_blank"> should
    // open in parallel with (or before) this fetch resolving.
    fetch(`${API}/api/lc/self-serve-intent`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* swallow · courtesy, never blocks */ });
  } catch {
    // Anything unexpected · swallow. This never impacts the member's click.
  }
}
